import React, { useMemo } from 'react'
import { useCartContext } from '../customHooks/useCart';
import { useSearchParams } from 'react-router-dom';
import LanguageToggle from './Headers/HeaderCMS/LanguageToggle';

type Props = {
    stage: string;
    language: string;
    country?: string;
    isMobile?: boolean;
    flow: string;
}

const CannabisProgressBar: React.FC<Props> = ({ stage, language, country, isMobile }) => {

    const {getCart} = useCartContext();
    const [searchParams] = useSearchParams();
    const cart = getCart();

    const stagesOrderMapping = useMemo(() => {
        const mapping: string[] = ['Questionnaire'];
        mapping.push('Treatment');

        if (cart.should_id_verify || stage === 'CustomerVerification') {
            mapping.push('CustomerVerification');
        }

        if (cart.consultation_type === 'video' || stage === 'chooseDoctorAndAppointment' || searchParams.get("ct") === "video") {
            mapping.push('chooseDoctorAndAppointment');
        }

        mapping.push('Shipping');
        mapping.push('Payment');

        return mapping.reduce((acc, stage, index) => {
            acc[stage] = index + 1;
            return acc;
        }, {} as Record<string, number>);
        
    }, [cart.consultation_type, cart.should_id_verify, stage, searchParams]);

    // Calculate current stage directly from stage prop - no state delay
    const currentStage = useMemo(() => {
        if (stage === "Questionnaire") return stagesOrderMapping['Questionnaire'] || 1;
        if (stage === "Treatment") return stagesOrderMapping['Treatment'] || 2;

        if (stage === "SignUp" && stagesOrderMapping['CustomerVerification']) return stagesOrderMapping['CustomerVerification'];
        if (stage === "SignUp" && !stagesOrderMapping['CustomerVerification']) return stagesOrderMapping['Shipping'] || 3;

        if (stage === "CustomerVerification") return stagesOrderMapping['CustomerVerification'] || 3;
        if (stage === "chooseDoctorAndAppointment") return stagesOrderMapping['chooseDoctorAndAppointment'] || 3;
        if (stage === "Shipping") return stagesOrderMapping['Shipping'] || 4;
        if (stage === "Payment") return stagesOrderMapping['Payment'] || 5;
        
        return 1; // Default to first stage
    }, [stage, stagesOrderMapping]);

    // Determine if video consultation step is included
    const hasVideoConsultation = cart.consultation_type === 'video' || stage === 'chooseDoctorAndAppointment' || searchParams.get("ct") === "video";
    
    // Scale down widths on mobile (multiply by 0.6 for mobile)
    const widthMultiplier = isMobile ? 0.6 : 1;
    
    // Define step configurations for 4-step and 5-step scenarios
    // Base 4 steps: Questionnaire, Treatment, Shipping, Payment
    // 5 steps (with video): Questionnaire, Treatment, chooseDoctorAndAppointment, Shipping, Payment
    const steps = useMemo(() => {
        // Base 4 steps configuration (no video consultation)
        const cannabisFlowSteps = [
            { width: 50, stageKey: 'Questionnaire' },   // First: short, dark teal-green
            { width: 200, stageKey: 'Treatment' },     // Second: longer, bright teal
            { width: 50, stageKey: 'Shipping' },      // Third: shipping
            { width: 50, stageKey: 'Payment' },         // Fourth: payment
        ];
        
        // 5 steps configuration (with video consultation)
        const cannabisFlowStepsWithVideoConsultation = [
            { width: 120, stageKey: 'Questionnaire' },   // First: short, dark teal-green
            { width: 220, stageKey: 'Treatment' },      // Second: longer, bright teal
            { width: 150, stageKey: 'chooseDoctorAndAppointment' }, // Third: video consultation
            { width: 130, stageKey: 'Shipping' },       // Fourth: shipping
            { width: 80, stageKey: 'Payment' },         // Fifth: payment
        ];
        
        // Select the appropriate configuration based on video consultation
        const stepConfigs = hasVideoConsultation ? cannabisFlowStepsWithVideoConsultation : cannabisFlowSteps;
        
        // Map to actual stages and calculate colors
        return stepConfigs.map((config, index) => {
            const stepNumber = index + 1;
            const stageNumber = stagesOrderMapping[config.stageKey] || stepNumber;
            const isCompleted = stageNumber < currentStage;
            const isCurrent = stageNumber === currentStage;
            
            // Completed steps use their assigned color, current step uses bright teal, future steps are grey
            let color = '#E5E5E5'; // Default grey
            if (isCompleted) {
                if (stepNumber === 1) {
                    color = '#1F9874'; // Dark teal for first completed 
                } else {
                    color = '#269C6E'; // Bright teal for other completed steps
                }
            } else if (isCurrent) {
                color = '#269C6E'; // Bright teal for current
            }

            return {
                stepNumber: stageNumber,
                width: Math.round(config.width * widthMultiplier),
                color,
                isCompleted,
                isCurrent
            };
        });
    }, [currentStage, widthMultiplier, hasVideoConsultation, stagesOrderMapping]);

    return (
        <div 
            key={`progress-bar-${stage}-${currentStage}`}
            id="cannabis-progress-bar-container" 
            className="w-full flex justify-center items-center" 
            style={{ 
                backgroundColor: '#F5F5F5',
                padding: isMobile ? '12px 16px' : '24px 16px',
                position: 'relative',
            }}
        >
            <div 
                id='cannabis-progress-bar-steps' 
                className='flex flex-row items-center' 
                style={{ 
                    gap: isMobile ? '4px' : '8px',
                    maxWidth: '100%',
                    overflow: 'hidden',
                }}
            >
                {steps.map((step, index) => (
                    <div
                        key={`step-${index}-${currentStage}`}
                        className="rounded-full flex-shrink-0"
                        style={{
                            width: `${step.width}px`,
                            height: isMobile ? '6px' : '8px',
                            backgroundColor: step.color,
                            borderRadius: '9999px',
                            transition: 'background-color 0.3s ease',
                        }}
                    />
                ))}
            </div>
            {country && (
                <div style={{ position: 'absolute', right: isMobile ? '12px' : '24px' }}>
                    <LanguageToggle country={country} language={language} />
                </div>
            )}
        </div>
    )
}

export default CannabisProgressBar
